import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useColorMode } from '@chakra-ui/react';
import { Button } from '@peacecover/component';
import { t } from 'i18next';
import ProfileAvatar from '../../my-page/components/ProfileAvatar';
import GradientBodyBox from '../../components/body/GradientBodyBox';
import { useBettieSearch } from '@pocapan/shared/domains/bettie/ui/hooks/useBettieSearch';
import { useBettieIntroAnimation } from '../hooks/useBettieIntroAnimation';
import { useBettieSettingInfo } from '../../hooks/useBettieSettingInfo';
import { useVirtualKeyboardVisible } from '../hooks/useVirtualKeyboardVisible';
import { useGetUserInfo } from '@pocapan/shared/domains/user/ui/hooks/useGetUserInfo';
import { BettieGradientColor } from '../../../constants/BettieGradientColor';
import type { components } from '@pocapan/shared/types/schema';
import type { BettieSettingStep } from '../BettieSettingPage';

type ApiV1MemberPersona = components['schemas']['ApiV1MemberPersona'];

interface BettieIntroPreviewProps {
  setCurrentStep: (currentStep: BettieSettingStep) => void;
  setBettiePersona: (bettiePersona: ApiV1MemberPersona) => void;
}

const BettieIntroPreview = ({ setCurrentStep, setBettiePersona }: BettieIntroPreviewProps) => {
  const { colorMode } = useColorMode();
  const { bettieSettingInfo, updateBettieSettingInfo } = useBettieSettingInfo();
  const bettieSearchResult = useBettieSearch(bettieSettingInfo.bettieNickname, { enabled: false });
  const { isKeyboardVisible, handleFocus, handleBlur } = useVirtualKeyboardVisible();
  const [searchParams] = useSearchParams();
  const isFirst = searchParams.get('isFirst') === 'true';

  const { data: user } = useGetUserInfo();

  // 인트로 애니메이션 훅 (교차 페이드 포함)
  const {
    showIntro,
    showBettieTitle,
    showDescription,
    moveToTop,
    overlayFadingOut,
    revealBaseContent,
    fadeStyle
  } = useBettieIntroAnimation(isFirst);

  const handleStart = useCallback(async () => {
    const input = bettieSettingInfo.bettieNickname.trim();
    if (!input) return;
    const { data } = await bettieSearchResult.refetch();
    const bettie = data?.bettie;

    if (!bettie) {
      setCurrentStep('persona_suggestion');
      return;
    }
    if (!bettie.member && !bettie.existChatRoom) {
      setCurrentStep('persona_suggestion');
      return;
    }
    setBettiePersona(bettie);
    setCurrentStep('loading');
  }, [bettieSearchResult, bettieSettingInfo.bettieNickname, setBettiePersona, setCurrentStep]);

  const showWarningText = user?.baseUserProfile?.nickname === bettieSettingInfo.bettieNickname.trim();

  return (
    <div className="min-h-screen pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] bg-background-light dark:bg-background-dark">
      {showIntro && (
        <BettieIntroAnimation
          moveToTop={moveToTop}
          showBettieTitle={showBettieTitle}
          showDescription={showDescription}
          overlayFadingOut={overlayFadingOut}
          fadeDurationMs={fadeStyle.fadeDurationMs}
        />
      )}
      <div
        className={`transition-opacity ${
          fadeStyle.fadeDurationMs ? `duration-[${fadeStyle.fadeDurationMs}ms]` : 'duration-500'
        } ${revealBaseContent ? 'opacity-100' : 'opacity-0'}`}
      >
        <div className="w-full px-[24px] mt-[105px] pb-[20px]">
          <div
            className="mx-[28px]"
            style={{
              borderTop: '6px solid transparent',
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTopLeftRadius: '40px',
              borderTopRightRadius: '40px',
              backgroundImage:
                colorMode === 'light'
                  ? 'linear-gradient(#fff, #fff),linear-gradient(to bottom, #E7E7E7, #E7E7E700)'
                  : 'linear-gradient(#fff, #fff),linear-gradient(to bottom, #151619, #4E4E4E00)',
              backgroundOrigin: 'border-box',
              backgroundClip: 'content-box, border-box'
            }}
          >
            <div className="px-[24px] pt-[37px] pb-[47px] bg-background-light dark:bg-background-dark rounded-t-[33px]">
              <div className="flex items-end gap-[4px]">
                <ProfileAvatar thumbnailUrl="" widthAndHeight="24px" svgWidthAndHeight="40px" />
                <div className="flex flex-col gap-[7px] mb-[-2px]">
                  <div className="bg-[linear-gradient(90deg,rgba(255,69,169,0.28)_0%,rgba(255,106,106,0.28)_100%)] p-[4px] w-fit">
                    <p className="text-[11px] font-semibold leading-[13px] bg-gradient-to-r from-[#FF45A9] to-[#FF6A6A] bg-clip-text text-transparent">
                      {bettieSettingInfo.bettieNickname
                        ? bettieSettingInfo.bettieNickname
                        : t('BettieSetting.bettieNickname.enterHere')}
                    </p>
                  </div>
                  <div className="py-[5px] px-[15px] rounded-[12px] bg-surface-lv2 w-fit">
                    <div className="typing-indicator mx-auto">
                      <span />
                      <span />
                      <span />
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-end justify-end mt-[1px] gap-[4px]">
                <p className="text-[11px] leading-[13px] text-text-main-lv4">
                  {t('BettieSetting.bettieNickname.me')}
                </p>
                <div className="flex gap-[4px] items-end">
                  <p className="text-[10px] leading-[12px] text-text-main-lv4">14:30</p>
                  <div className="px-[13px] py-[10px] rounded-[16px] bg-primary-light dark:bg-primary-dark mb-[2px]">
                    <p className="text-[13px] leading-[16px] text-pure-white">
                      {t('BettieSetting.bettieNickname.hello')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <GradientBodyBox
            title={t('BettieSetting.bettieNickname.title')}
            description={t('BettieSetting.bettieNickname.description') as string}
            placeholder={t('BettieSetting.bettieNickname.placeholder')}
            value={bettieSettingInfo.bettieNickname}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={(e) => updateBettieSettingInfo({ bettieNickname: e.target.value })}
            className="mt-[8px]"
            showWarningText={showWarningText}
            warningText={t('BettieSetting.bettieNickname.warningText') as string}
          />
        </div>
        <div
          className={`fixed pb-[calc(10px+env(safe-area-inset-bottom))] bottom-0 left-0 right-0 w-full px-[24px] transition-all duration-150 ease-in-out ${
            isKeyboardVisible ? 'translate-y-full opacity-0' : 'translate-y-0 opacity-100'
          }`}
        >
          <p className="text-[12px] font-medium text-text-sub-lv1 leading-[16px] mb-[17px] text-center whitespace-pre-line">
            {t('BettieSetting.bettieDescription')}
          </p>
          <Button
            size="large"
            className="w-full text-[17px] font-semibold"
            disabled={!bettieSettingInfo.bettieNickname.trim() || showWarningText}
            onClick={handleStart}
          >
            {t('BettieSetting.bettieNickname.disabledStartButton')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BettieIntroPreview;

const BettieIntroAnimation = ({
  moveToTop,
  showBettieTitle,
  showDescription,
  overlayFadingOut,
  fadeDurationMs
}: {
  moveToTop: boolean;
  showBettieTitle: boolean;
  showDescription: boolean;
  overlayFadingOut: boolean;
  fadeDurationMs: number;
}) => {
  const { colorMode } = useColorMode();
  return (
    <div
      className={`fixed inset-0 z-[1000] flex flex-col items-center justify-center bg-background-elevated transition-opacity ${overlayFadingOut ? 'opacity-0' : 'opacity-100'}`}
      style={{
        transitionDuration: `${fadeDurationMs}ms`
      }}
    >
      <div
        className="w-full relative flex flex-col items-center justify-center transition-all duration-1000 ease-in-out"
        style={{
          transform: moveToTop ? 'translateY(-200px)' : 'translateY(0)'
        }}
      >
        <div
          className={`w-[240px] h-[105px] rounded-full relative transition-all duration-1200 ease-out ${
            showBettieTitle ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
          }`}
        >
          <div
            aria-hidden="true"
            style={BettieGradientColor.titleBackground[colorMode]}
            className="absolute inset-0 rounded-full pointer-events-none"
          />
          <img
            src={`/assets/bettie/bettie_title_${colorMode}.png`}
            alt="Bettie"
            className="w-[158px] h-[74px] absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] z-10"
          />
        </div>

        <p
          className={`w-full text-center text-text-main-lv2 font-bold text-[17px] leading-[20px] transition-all duration-1000 ease-out ${
            showDescription ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
          }`}
        >
          {t('BettieSetting.description')}
        </p>
      </div>
    </div>
  );
};